import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { formatAmount } from "@/utils/expenseFormatters";
import { AccountRow, SubAccountRow } from "./interfaces";
import { getAccountTypeColor } from "./utils";
import { format, parseISO } from "date-fns";
import { AccountLineItem } from "@blinkbooks/types";
import apiClient from "@/utils/httpClient";

interface AccountDetailSheetProps {
  account: AccountRow | null;
  onClose: () => void;
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return null;
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch {
    return null;
  }
};

const STATUS_STYLES: Record<string, string> = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  open: "bg-sky-50 text-sky-700 border-sky-200",
  unpaid: "bg-orange-50 text-orange-700 border-orange-200",
  partially_paid: "bg-amber-50 text-amber-700 border-amber-200",
  overdue: "bg-red-50 text-red-700 border-red-200",
  draft: "bg-gray-50 text-gray-600 border-gray-200",
  pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
  void: "bg-gray-50 text-gray-400 border-gray-200",
  MATCHED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  ADDED: "bg-sky-50 text-sky-700 border-sky-200",
  IGNORED: "bg-gray-50 text-gray-400 border-gray-200",
};

const TYPE_LABELS: Record<string, string> = {
  invoice: "Invoice",
  bill: "Bill",
  expense: "Expense",
  bank_transaction: "Transaction",
};

const SubAccountLine = ({ sub, level = 1 }: { sub: SubAccountRow; level?: number }) => {
  const typeColors = getAccountTypeColor(sub.type);
  return (
    <>
      <div
        className="flex items-center justify-between py-2 border-b border-border/30 last:border-0"
        style={{ paddingLeft: `${(level - 1) * 1.25 + 0.5}rem` }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {level > 1 && (
            <span className="text-muted-foreground/40 text-xs select-none">└</span>
          )}
          <div className="min-w-0">
            <div className="text-sm font-medium text-foreground truncate">{sub.name}</div>
            {sub.accountNumber && (
              <div className="text-xs text-muted-foreground">#{sub.accountNumber}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${typeColors.bg} ${typeColors.text} border ${typeColors.border}`}>
            {sub.type}
          </span>
          <span className="text-sm font-semibold tabular-nums text-foreground w-24 text-right">
            {sub.balance !== undefined ? formatAmount(sub.balance) : <span className="text-muted-foreground">—</span>}
          </span>
        </div>
      </div>
      {sub.subAccounts?.map((child) => (
        <SubAccountLine key={child.id} sub={child} level={level + 1} />
      ))}
    </>
  );
};

const AccountDetailSheet = ({ account, onClose }: AccountDetailSheetProps) => {
  const [lineItems, setLineItems] = useState<AccountLineItem[]>([]);
  const [lineItemsLoading, setLineItemsLoading] = useState(false);

  useEffect(() => {
    if (!account) {
      setLineItems([]);
      return;
    }
    let cancelled = false;
    setLineItemsLoading(true);
    setLineItems([]);
    apiClient
      .get<AccountLineItem[]>(`/accounts/${account.id}/line-items`)
      .then((data) => {
        if (!cancelled) setLineItems(data);
      })
      .catch(() => {
        if (!cancelled) setLineItems([]);
      })
      .finally(() => {
        if (!cancelled) setLineItemsLoading(false);
      });
    return () => { cancelled = true; };
  }, [account?.id]);

  if (!account) return null;

  const typeColors = getAccountTypeColor(account.type);
  const subAccounts = account.subAccounts ?? [];
  const createdAt = formatDate(account.createdAt);
  const updatedAt = formatDate(account.updatedAt);
  const lineItemsTotal = lineItems.reduce((s, item) => s + item.amount, 0);

  return (
    <Sheet open={!!account} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-xl leading-tight">{account.name}</SheetTitle>
              {account.accountNumber && (
                <p className="text-sm text-muted-foreground mt-0.5">Account #{account.accountNumber}</p>
              )}
            </div>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold shrink-0 ${typeColors.bg} ${typeColors.text} border ${typeColors.border}`}>
              {account.type}
            </span>
          </div>
        </SheetHeader>

        <div className="py-5 space-y-6">
          {/* Balance */}
          <div className="bg-muted/30 rounded-xl p-4 flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Current Balance</span>
            <span className="text-2xl font-bold tabular-nums text-foreground">
              {account.balance !== undefined ? formatAmount(account.balance) : "—"}
            </span>
          </div>

          {/* Metadata */}
          {(createdAt || updatedAt) && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              {createdAt && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Created</p>
                  <p className="font-medium">{createdAt}</p>
                </div>
              )}
              {updatedAt && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Last Updated</p>
                  <p className="font-medium">{updatedAt}</p>
                </div>
              )}
            </div>
          )}

          {/* Sub-accounts */}
          {subAccounts.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Sub-accounts
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  ({subAccounts.length})
                </span>
              </h3>
              <div className="rounded-lg border border-border/40 bg-card overflow-hidden">
                {subAccounts.map((sub) => (
                  <SubAccountLine key={sub.id} sub={sub} level={1} />
                ))}
              </div>

              <div className="mt-3 flex items-center justify-between px-1">
                <span className="text-xs text-muted-foreground">
                  Total across {subAccounts.length} sub-account{subAccounts.length !== 1 ? "s" : ""}
                </span>
                <span className="text-sm font-semibold tabular-nums">
                  {formatAmount(
                    subAccounts.reduce((sum, s) => sum + (s.balance ?? 0), 0)
                  )}
                </span>
              </div>
            </div>
          )}

          {subAccounts.length === 0 && !lineItemsLoading && lineItems.length === 0 && (
            <p className="text-sm text-muted-foreground italic">No sub-accounts</p>
          )}

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground">
                Contributing Transactions
                {!lineItemsLoading && lineItems.length > 0 && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    ({lineItems.length})
                  </span>
                )}
              </h3>
              {!lineItemsLoading && lineItems.length > 0 && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  Total: {formatAmount(lineItemsTotal)}
                </span>
              )}
            </div>

            {lineItemsLoading && (
              <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Loading transactions...</span>
              </div>
            )}

            {!lineItemsLoading && lineItems.length === 0 && (
              <p className="text-sm text-muted-foreground italic py-2">No transactions found for this account.</p>
            )}

            {!lineItemsLoading && lineItems.length > 0 && (
              <div className="rounded-lg border border-border/40 bg-card overflow-hidden">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border/40 bg-muted/30">
                      <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item) => (
                      <tr key={`${item.type}-${item.id}`} className="border-b border-border/20 last:border-0 hover:bg-muted/10 transition-colors">
                        <td className="py-2 px-3 text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(item.date) ?? item.date}
                        </td>
                        <td className="py-2 px-3 max-w-[180px]">
                          <div className="truncate text-xs text-foreground/80" title={item.description}>
                            {item.description}
                          </div>
                          {item.counterparty && (
                            <div className="truncate text-xs text-muted-foreground/70" title={item.counterparty}>
                              {item.counterparty}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex flex-col gap-1">
                            <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-muted/50 text-muted-foreground border border-border/30 w-fit">
                              {TYPE_LABELS[item.type] ?? item.type}
                            </span>
                            {item.status && (
                              <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium border w-fit ${STATUS_STYLES[item.status] ?? "bg-gray-50 text-gray-500 border-gray-200"}`}>
                                {item.status.replace(/_/g, " ")}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-right font-semibold tabular-nums text-foreground/90 whitespace-nowrap">
                          {formatAmount(item.amount, item.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AccountDetailSheet;
